import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiX, FiArrowRight, FiHome, FiUser, FiBriefcase, FiMessageCircle } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';

const CommandPaletteOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;

  @media (max-width: 768px) {
    padding-top: 5vh;
    align-items: flex-start;
  }
`;

const CommandPaletteContainer = styled(motion.div)`
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(139, 92, 246, 0.1));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 95%;
    max-width: none;
    margin: 0 1rem;
    border-radius: 12px;
  }
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 0.75rem;
  }
`;

const SearchIcon = styled(FiSearch)`
  color: #8b5cf6;
  font-size: 1.2rem;
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1.1rem;
  outline: none;
  font-weight: 500;

  &::placeholder {
    color: #94a3b8;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const ResultsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;

  @media (max-width: 768px) {
    max-height: 60vh;
  }
`;

const ResultItem = styled(motion.div)<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isSelected ? 'rgba(139, 92, 246, 0.2)' : 'transparent'};
  border-left: 3px solid ${props => props.isSelected ? '#8b5cf6' : 'transparent'};

  &:hover {
    background: rgba(139, 92, 246, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const ResultIcon = styled.div`
  color: #8b5cf6;
  margin-right: 1rem;
  font-size: 1.1rem;
`;

const ResultContent = styled.div`
  flex: 1;
`;

const ResultTitle = styled.div`
  color: #ffffff;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ResultDescription = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ResultArrow = styled(FiArrowRight)`
  color: #8b5cf6;
  font-size: 1rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  ${ResultItem}:hover & {
    opacity: 1;
  }
`;

const NoResults = styled.div`
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
  font-style: italic;
`;

const ShortcutHint = styled.div`
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  font-size: 0.85rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.75rem;
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
`;

const ShortcutKey = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.8rem;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
`;

interface CommandItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  keywords: string[];
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState<CommandItem[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  const localCommands = useMemo(() => [
    {
      id: 'home',
      title: 'Go to Home',
      description: 'Navigate to the homepage',
      icon: <FiHome />,
      action: () => navigate('/'),
      keywords: ['home', 'main', 'landing', 'start']
    },
    {
      id: 'about',
      title: 'About Section',
      description: 'Learn more about Briddy',
      icon: <FiUser />,
      action: () => {
        if (location.pathname === '/') {
          document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate('/', { state: { sectionId: 'about' } });
        }
        onClose();
      },
      keywords: ['about', 'profile', 'bio', 'who', 'me']
    },
    {
      id: 'projects',
      title: 'Projects Section',
      description: 'View my portfolio projects',
      icon: <FiBriefcase />,
      action: () => {
        if (location.pathname === '/') {
          document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate('/', { state: { sectionId: 'projects' } });
        }
        onClose();
      },
      keywords: ['projects', 'work', 'portfolio', 'code', 'apps']
    },
    {
      id: 'contact',
      title: 'Contact Section',
      description: 'Get in touch with me',
      icon: <FiMessageCircle />,
      action: () => {
        if (location.pathname === '/') {
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate('/', { state: { sectionId: 'contact' } });
        }
        onClose();
      },
      keywords: ['contact', 'email', 'message', 'reach', 'connect']
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      description: 'View privacy policy',
      icon: <FiUser />,
      action: () => {
        navigate('/privacy-policy');
        onClose();
      },
      keywords: ['privacy', 'policy', 'legal', 'terms']
    },
    {
      id: 'terms',
      title: 'Terms of Service',
      description: 'View terms of service',
      icon: <FiUser />,
      action: () => {
        navigate('/terms-of-service');
        onClose();
      },
      keywords: ['terms', 'service', 'legal', 'conditions']
    }
  ], [navigate, location, onClose]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) {
      setResults(localCommands);
      setSelectedIndex(0);
      return;
    }

    const filtered = localCommands.filter(command =>
      command.keywords.some(keyword =>
        keyword.toLowerCase().includes(query.toLowerCase())
      ) ||
      command.title.toLowerCase().includes(query.toLowerCase()) ||
      command.description.toLowerCase().includes(query.toLowerCase())
    );

    setResults(filtered);
    setSelectedIndex(0);
  }, [query, localCommands]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < results.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : results.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            results[selectedIndex].action();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleResultClick = (command: CommandItem) => {
    command.action();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <CommandPaletteOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <CommandPaletteContainer
            initial={{ scale: 0.9, opacity: 0, y: -20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: -20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <SearchInput>
              <SearchIcon />
              <Input
                ref={inputRef}
                placeholder="Search commands..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <CloseButton onClick={onClose}>
                <FiX />
              </CloseButton>
            </SearchInput>

            <ResultsContainer>
              {results.length > 0 ? (
                results.map((command, index) => (
                  <ResultItem
                    key={command.id}
                    isSelected={index === selectedIndex}
                    onClick={() => handleResultClick(command)}
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <ResultIcon>{command.icon}</ResultIcon>
                    <ResultContent>
                      <ResultTitle>{command.title}</ResultTitle>
                      <ResultDescription>{command.description}</ResultDescription>
                    </ResultContent>
                    <ResultArrow />
                  </ResultItem>
                ))
              ) : (
                <NoResults>No commands found for "{query}"</NoResults>
              )}
            </ResultsContainer>

            <ShortcutHint>
              <span>Use arrow keys to navigate, Enter to select</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <ShortcutKey>↑</ShortcutKey>
                <ShortcutKey>↓</ShortcutKey>
                <ShortcutKey>Enter</ShortcutKey>
                <ShortcutKey>Esc</ShortcutKey>
              </div>
            </ShortcutHint>
          </CommandPaletteContainer>
        </CommandPaletteOverlay>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette; 